import React, { useEffect, useState } from "react";
import { X, Save, Send, Download, ChevronDown, Edit } from "lucide-react";
import { toast } from "wc-toast";
import ImageMaskEditor from "./ImageMaskEditor";
import ImagePreviewGrid from "./ImagePreviewGrid";

export default function ImagePreviewModal({
  logo_enable,
  make_enable_logo,
  position,
  setPosition,

  open,
  onClose,
  imageUrl,
  prompt,
  negativePrompt,
  style,
  shape,
  send,
  save,
  setNewBase64,
  base64,
  onConfirm,
  selectedRow,
  onEdit,
  isSend,
  trackingPhotos,
  active,
  setBase64,
  setUrlOfImage,
  setActive,
}) {
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [showNegative, setShowNegative] = React.useState(false);
  const [logoDropdownOpen, setLogoDropdownOpen] = useState(false);

  const logoPositions = [
    "top-left",
    "top-center",
    "top-right",
    "bottom-left",
    "bottom-center",
    "bottom-right",
  ];

  const isMobile = window.innerWidth < 768;

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.85)",
      zIndex: 9999,
      display: "flex",
      alignItems: isMobile ? "flex-end" : "center",
      justifyContent: "center",
      padding: isMobile ? 0 : 0,
      overflowY: isMobile ? "auto" : "hidden",
      WebkitOverflowScrolling: "touch",
    },

    // ✅ MOBILE scroll: modal scrolls; desktop unchanged
    modal: {
      width: isMobile ? "100%" : 1120,
      height: isMobile ? "100svh" : "auto",
      maxHeight: isMobile ? "100svh" : "92vh",
      background: "linear-gradient(180deg, #0B0F14, #0F1419)",
      borderRadius: isMobile ? "18px 18px 0 0" : 26,
      border: "1px solid #2B3440",
      boxShadow: "0 40px 80px rgba(0,0,0,0.6)",
      position: "relative",

      // ✅ DESKTOP: keep your row layout
      display: isMobile ? "block" : "flex",
      flexDirection: isMobile ? "column" : "row",

      // ✅ MOBILE: modal itself scrolls
      overflowX: "hidden",
      overflowY: isMobile ? "auto" : "hidden",
      WebkitOverflowScrolling: "touch",
    },

    closeBtn: {
      position: "absolute",
      top: 12,
      right: 12,
      cursor: "pointer",
      color: "#F7941D",
      zIndex: 1000,
    },

    dropdown: {
      width: "100%",
      borderRadius: 14,
      border: "2px solid #2B3440",
      padding: isMobile ? "12px 14px" : "14px 18px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      background: "#0F1419",
      color: "#fff",
      boxSizing: "border-box",
    },

    // ✅ mobile opens down
    dropdownMenu: {
      position: "absolute",
      top: isMobile ? 54 : "auto",
      bottom: isMobile ? "auto" : 58,
      left: 0,
      width: "100%",
      background: "#0F1419",
      borderRadius: 14,
      border: "2px solid #2B3440",
      maxHeight: isMobile ? 160 : 220,
      overflowY: "auto",
      zIndex: 9999,
      boxShadow: "0 18px 40px rgba(0,0,0,0.55)",
    },

    dropdownItem: {
      padding: isMobile ? "12px 14px" : "14px 16px",
      fontSize: isMobile ? 14 : 15,
      cursor: "pointer",
      color: "#E5E7EB",
      borderBottom: "1px solid #1F2937",
      textAlign: "left",
    },

    dropdownItemActive: {
      background: "linear-gradient(90deg, #EE4037, #F7941D)",
      color: "#fff",
      fontWeight: 600,
    },

    imageSection: {
      flex: isMobile ? "0 0 auto" : 3,
      height: isMobile ? "auto" : "auto",
      padding: isMobile ? 10 : 16,
      display: "flex",
      alignItems: "stretch",
      justifyContent: "center",
      background: "#0B0F14",
      borderBottom: isMobile ? "1px solid #2B3440" : "none",
    },

    mobileActionBar: {
      display: isMobile ? "flex" : "none",
      justifyContent: "space-around",
      alignItems: "center",
      padding: 10,
      height: 56,
      background: "#121821",
      borderTop: "1px solid #2B3440",
      boxSizing: "border-box",
      width: "100%",
    },

    // ✅ fix right cut off: width 100 + border-box + minWidth 0 + overflowX hidden
    content: {
      width: "100%",
      boxSizing: "border-box",
      minWidth: 0,
      padding: isMobile ? 12 : 18,
      display: "flex",
      flexDirection: "column",
      gap: 12,
      color: "#fff",
      overflowY: isMobile ? "visible" : "auto",
      overflowX: "hidden",
      maxHeight: isMobile ? "none" : "auto",
    },

    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      paddingBottom: 10,
      borderBottom: "1px solid #1F2937",
      fontSize: isMobile ? 14 : 16,
      width: "100%",
      boxSizing: "border-box",
      minWidth: 0,
    },

    title: {
      fontSize: isMobile ? 14 : 16,
      fontWeight: 700,
    },

    actionBar: {
      display: isMobile ? "none" : "flex",
      gap: 8,
    },

    actionBtn: {
      width: 40,
      height: 40,
      borderRadius: 10,
      background: "#121821",
      border: "1px solid #2B3440",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#F7941D",
    },

    // ✅ fix right cut off
    collapsible: {
      width: "100%",
      boxSizing: "border-box",
      background: "#121821",
      border: "1px solid #2B3440",
      borderRadius: 14,
      overflow: "hidden",
    },

    collapseHeader: {
      padding: isMobile ? "10px 12px" : "14px 16px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: isMobile ? 14 : 15,
      width: "100%",
      boxSizing: "border-box",
      minWidth: 0,
    },

    collapseBody: (open) => ({
      maxHeight: open ? 260 : 0,
      opacity: open ? 1 : 0,
      transition: "all 0.25s ease",
      overflow: "hidden",
    }),

    // ✅ fix right cut off
    textarea: {
      width: "100%",
      boxSizing: "border-box",
      minHeight: 120,
      background: "#0F1419",
      border: "none",
      padding: 12,
      color: "#E5E7EB",
      resize: "none",
      outline: "none",
      fontSize: 14,
      lineHeight: 1.6,
      overflowX: "hidden",
    },

    // ✅ fix right cut off
    meta: {
      width: "100%",
      boxSizing: "border-box",
      background: "#121821",
      border: "1px solid #2B3440",
      borderRadius: 12,
      padding: "8px 12px",
      fontSize: 12,
      color: "#D1D5DB",
    },
  };

  function downloadImage(url, fileName = "AI-image.png") {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Image downloaded successfully");
  }

  const [localPrompt, setLocalPrompt] = React.useState("");

  useEffect(() => {
    if (base64 == null) setLocalPrompt("");
  }, [base64]);

  if (!open) return null;

  const logoToggleStyles = {
    container: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      cursor: "pointer",
      flexWrap: isMobile ? "wrap" : "nowrap",
    },
    switch: {
      position: "relative",
      width: 50,
      height: 26,
      borderRadius: 14,
      transition: "background 0.3s",
      flexShrink: 0,
      background: "#4B5563",
    },
    switchOn: {
      background: "linear-gradient(135deg, #FFB347, #FF7A00)",
    },
    knob: {
      position: "absolute",
      top: 2,
      width: 22,
      height: 22,
      borderRadius: "50%",
      background: "#fff",
      boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
      transition: "left 0.3s",
    },
    label: {
      fontSize: 14,
      fontWeight: 600,
      color: "#E5E7EB",
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <wc-toast></wc-toast>

      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <X size={24} style={styles.closeBtn} onClick={onClose} />

        {/* IMAGE */}
        <div style={styles.imageSection}>
          <div
            id={base64}
            style={{
              padding: isMobile ? 10 : 18,
              overflowY: isMobile ? "visible" : "auto",
              flex: isMobile ? "none" : 2,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <textarea
              value={localPrompt}
              onChange={(e) => setLocalPrompt(e.target.value)}
              placeholder="Refine your prompt..."
              style={{
                width: "100%",
                minHeight: isMobile ? 54 : 80,
                background: "#ECFFFF",
                borderRadius: 10,
                border: "2px solid #4EEFEF",
                padding: isMobile ? 12 : 14,
                fontSize: isMobile ? 14 : 15,
                outline: "none",
                boxSizing: "border-box",
                marginBottom: isMobile ? 10 : 14,
              }}
            />

            <ImageMaskEditor
              key={base64}
              image={`data:image/png;base64,${base64}`}
              onChangeBase64={setNewBase64}
            />

            {base64 != null ? (
              <div
                style={{
                  padding: isMobile ? 12 : 16,
                  borderTop: "1px solid #2B3440",
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "stretch" : "center",
                  justifyContent: "flex-end",
                  gap: 12,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    flexDirection: "row",
                    display: "flex",
                    flexWrap: isMobile ? "wrap" : "nowrap",
                    gap: 12,
                    width: "100%",
                    boxSizing: "border-box",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={logoToggleStyles.container}
                    onClick={() => make_enable_logo(!logo_enable)}
                  >
                    <div
                      style={{
                        ...logoToggleStyles.switch,
                        ...(logo_enable ? logoToggleStyles.switchOn : {}),
                      }}
                    >
                      <div
                        style={{
                          ...logoToggleStyles.knob,
                          left: logo_enable ? 26 : 2,
                        }}
                      />
                    </div>

                    <span style={logoToggleStyles.label}>
                      {logo_enable ? "Logo Enabled" : "Logo Disabled"}
                    </span>
                  </div>

                  {logo_enable && (
                    <div
                      style={{
                        width: isMobile ? "100%" : 200,
                        marginTop: 8,
                        position: "relative",
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={styles.dropdown}
                        onClick={() => setLogoDropdownOpen(!logoDropdownOpen)}
                      >
                        <span style={{ color: position ? "#fff" : "#9CA3AF" }}>
                          {position
                            ? position.replace("-", " ")
                            : "Choose Logo Position..."}
                        </span>
                        <ChevronDown size={18} color="#F7941D" />
                      </div>
                      {logoDropdownOpen && (
                        <div style={styles.dropdownMenu}>
                          {logoPositions.map((pos) => (
                            <div
                              key={pos}
                              onClick={() => {
                                setPosition(pos);
                                setLogoDropdownOpen(false);
                              }}
                              style={{
                                ...styles.dropdownItem,
                                ...(position === pos
                                  ? styles.dropdownItemActive
                                  : {}),
                              }}
                            >
                              {pos.replace("-", " ")}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  style={{
                    ...modalStyles.cancelBtn,
                    width: isMobile ? "100%" : "auto",
                  }}
                  onClick={onClose}
                >
                  Cancel
                </button>

                <button
                  style={{
                    ...modalStyles.confirmBtn,
                    width: isMobile ? "100%" : "auto",
                  }}
                  onClick={() => onConfirm(localPrompt)}
                >
                  Confirm Regenerate
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {/* MOBILE ACTION BUTTONS */}
        <div style={styles.mobileActionBar}>
          <div onClick={save} style={styles.actionBtn}>
            <Save size={18} />
          </div>
          {isSend ? (
            <div onClick={send} style={styles.actionBtn}>
              <Send size={18} />
            </div>
          ) : null}

          <div onClick={() => downloadImage(imageUrl)} style={styles.actionBtn}>
            <Download size={18} />
          </div>
        </div>

        {/* CONTENT */}
        <div style={styles.content}>
          <div style={styles.header}>
            <div style={styles.title}>Image Preview</div>

            {/* DESKTOP ACTION BUTTONS */}
            <div style={styles.actionBar}>
              <div onClick={save} style={styles.actionBtn}>
                <Save size={16} />
              </div>
              {isSend ? (
                <div onClick={send} style={styles.actionBtn}>
                  <Send size={16} />
                </div>
              ) : null}

              <div
                onClick={() => downloadImage(imageUrl)}
                style={styles.actionBtn}
              >
                <Download size={16} />
              </div>
            </div>
          </div>

          {/* PROMPT */}
          <div style={styles.collapsible}>
            <div
              style={styles.collapseHeader}
              onClick={() => setShowPrompt(!showPrompt)}
            >
              Prompt
              <ChevronDown
                size={18}
                style={{
                  transform: showPrompt ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "0.2s",
                }}
              />
            </div>
            <div style={styles.collapseBody(showPrompt)}>
              <textarea readOnly style={styles.textarea} value={prompt} />
            </div>
          </div>

          {/* NEGATIVE PROMPT */}
          <div style={styles.collapsible}>
            <div
              style={styles.collapseHeader}
              onClick={() => setShowNegative(!showNegative)}
            >
              Negative Prompt
              <ChevronDown
                size={18}
                style={{
                  transform: showNegative ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "0.2s",
                }}
              />
            </div>
            <div style={styles.collapseBody(showNegative)}>
              <textarea
                readOnly
                style={styles.textarea}
                value={negativePrompt || "—"}
              />
            </div>
          </div>

          <div style={styles.meta}>Style: {style}</div>
          <div style={styles.meta}>Shape: {shape}</div>

          {/* ✅ wrap to ensure never overflows in mobile */}
          <div style={{ width: "100%", boxSizing: "border-box", minWidth: 0 }}>
            <ImagePreviewGrid
              setNewBase64={setNewBase64}
              setActive={setActive}
              setUrlOfImage={setUrlOfImage}
              active={active}
              setBase64={setBase64}
              images={trackingPhotos}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
    padding: 16,
  },
  modal: {
    width: "96%",
    maxWidth: 1100,
    maxHeight: "90vh",
    background: "#0B0F14",
    borderRadius: 16,
    border: "1px solid #2B3440",
    boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    padding: "14px 18px",
    borderBottom: "1px solid #2B3440",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "white",
  },
  textarea: {
    width: "100%",
    minHeight: 80,
    background: "#ECFFFF",
    borderRadius: 10,
    border: "2px solid #4EEFEF",
    padding: 14,
    fontSize: 15,
    outline: "none",
    boxSizing: "border-box",
    marginBottom: 14,
  },
  content: {
    padding: 18,
    overflowY: "auto",
    flex: 1,
    overflowX: "hidden",
  },
  editorWrap: {
    background: "#121821",
    borderRadius: 14,
    border: "1px solid #2B3440",
    padding: 12,
  },
  actions: {
    padding: "14px 18px",
    borderTop: "1px solid #2B3440",
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
  },
  cancelBtn: {
    background: "#121821",
    color: "#E5E7EB",
    border: "1px solid #2B3440",
    borderRadius: 10,
    padding: "10px 18px",
    fontWeight: 600,
    cursor: "pointer",
  },
  confirmBtn: {
    background: "linear-gradient(135deg, #FFB347, #FF7A00)",
    color: "#000",
    border: "none",
    borderRadius: 10,
    padding: "10px 22px",
    fontWeight: 800,
    cursor: "pointer",
  },
};